import React from 'react';
import { Container, Card, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const RefundPolicy = () => {
  return (
    <>
      <SEO 
        title="Cancellation & Refund Policy - IT World"
        description="Read our cancellation and refund policy for software license purchases at IT World."
      />
      <div className="bg-primary text-white py-5">
        <Container>
          <h1 className="text-center mb-3">Cancellation & Refund Policy</h1>
          <p className="text-center lead">Last Updated: February 2025</p>
        </Container>
      </div>

      <Container className="py-5">
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-4 p-md-5">
            <Alert variant="info" className="mb-4">
              <strong>Important:</strong> Please read this policy carefully before making a purchase. 
              By purchasing from IT World, you agree to these terms.
            </Alert>

            <section className="mb-5">
              <h2 className="h4 mb-3">1. Digital Products - Software Licenses</h2>
              <p>
                Since we sell digital products (software license keys), our refund policy is designed 
                to protect both our customers and our business from misuse.
              </p>
              <h5 className="mt-4">1.1 Eligibility for Refund</h5>
              <p>You may be eligible for a refund in the following cases:</p>
              <ul>
                <li>
                  <strong>Invalid or Non-Working Keys:</strong> If the license key provided does not 
                  work or is invalid, and our support team cannot resolve the issue within 48 hours.
                </li>
                <li>
                  <strong>Duplicate Purchase:</strong> If you accidentally purchased the same product 
                  twice, we will refund one of the purchases (provided the key has not been activated).
                </li>
                <li>
                  <strong>Technical Issues:</strong> If due to a technical error on our end, you 
                  did not receive your license key and we are unable to deliver it within 72 hours.
                </li>
              </ul>

              <h5 className="mt-4">1.2 Non-Refundable Cases</h5>
              <p>Refunds will NOT be provided in the following situations:</p>
              <ul>
                <li>The license key has been activated or used</li>
                <li>Change of mind after purchase</li>
                <li>Incompatibility with your system (please check requirements before purchase)</li>
                <li>Purchase of wrong product due to customer error</li>
                <li>Failure to activate within the specified timeframe mentioned in product description</li>
                <li>Issues caused by third-party software or system configurations</li>
              </ul>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">2. Cancellation Policy</h2>
              <h5>2.1 Before Payment</h5>
              <p>
                You can cancel your order at any time before completing the payment. Simply close 
                the payment window or do not proceed with the payment.
              </p>

              <h5 className="mt-4">2.2 After Payment</h5>
              <p>
                Once payment is completed and the license key is delivered, the order cannot be 
                cancelled. This is because digital products (license keys) are delivered instantly 
                and cannot be "returned" in the traditional sense.
              </p>

              <h5 className="mt-4">2.3 Failed Transactions</h5>
              <p>
                If your payment fails but the amount is deducted from your account, please wait 
                for 5-7 business days for the automatic refund from your bank. If the amount is 
                not refunded, please contact us with your transaction details.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">3. Refund Process</h2>
              <h5>3.1 How to Request a Refund</h5>
              <ol>
                <li>Contact us within 7 days of purchase at <a href="mailto:itworldwani@gmail.com">itworldwani@gmail.com</a></li>
                <li>Provide your Order ID and reason for refund request</li>
                <li>Include any relevant screenshots or error messages</li>
                <li>Our team will review your request within 2-3 business days</li>
              </ol>

              <h5 className="mt-4">3.2 Refund Timeline</h5>
              <p>
                Once approved, refunds will be processed within 5-7 business days. The refund 
                will be credited to the original payment method used for the purchase.
              </p>
              <ul>
                <li><strong>UPI/Net Banking:</strong> 3-5 business days</li>
                <li><strong>Credit/Debit Card:</strong> 5-7 business days</li>
                <li><strong>Wallet:</strong> 1-3 business days</li>
              </ul>

              <h5 className="mt-4">3.3 Partial Refunds</h5>
              <p>
                In some cases, we may offer a partial refund or store credit instead of a full 
                refund, depending on the circumstances of the request.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">4. Replacement Policy</h2>
              <p>
                If you receive a non-working license key, we will first attempt to provide a 
                replacement key before processing a refund. Replacements are subject to availability.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">5. Development Services</h2>
              <p>
                For custom development services (web development, mobile apps, etc.), the 
                cancellation and refund policy will be defined in the project agreement/contract 
                signed between IT World and the client.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">6. Contact Us</h2>
              <p>
                If you have any questions about our Cancellation & Refund Policy, please contact us:
              </p>
              <ul className="list-unstyled">
                <li><strong>Email:</strong> <a href="mailto:itworldwani@gmail.com">itworldwani@gmail.com</a></li>
                <li><strong>Phone:</strong> +91 9561306384</li>
                <li><strong>Business Hours:</strong> Monday - Saturday, 11:00 AM - 7:00 PM IST</li>
              </ul>
            </section>

            <section>
              <h2 className="h4 mb-3">7. Policy Updates</h2>
              <p>
                IT World reserves the right to modify this Cancellation & Refund Policy at any time. 
                Changes will be effective immediately upon posting on this page. We encourage you 
                to review this policy periodically.
              </p>
            </section>

            <hr className="my-5" />

            <div className="text-center">
              <p className="text-muted mb-3">
                By making a purchase on our website, you acknowledge that you have read, 
                understood, and agree to this Cancellation & Refund Policy.
              </p>
              <Link to="/contact" className="btn btn-primary">
                Contact Support
              </Link>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default RefundPolicy;
